import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button, ButtonGroup, Card, Spinner } from 'react-bootstrap';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from 'components/common/ReactEchart';
import { useAppContext } from 'providers/AppProvider';
import { avgRequest } from 'hooks/CallApi';
import { formatCountStyle3 } from 'components/common/SunnyUtils';
import { formatAverageResponseTime } from 'components/common/SunnyUtils';
import { getCompanyColor, getCompanyColorRgba } from 'helpers/companyColors';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);

// const dataSets = {

//   barData: [
//     { name: '고객사 A', process: 30, min: 10, max: 50 },
//     { name: '고객사 B', process: 60, min: 20, max: 100 },
//     { name: '고객사 C', process: 300, min: 50, max: 550 },
//     { name: '고객사 D', process: 600, min: 100, max: 1100 },
//     { name: '고객사 E', process: 1200, min: 200, max: 2200 }
//   ]

// };

export default function PolicyCharts() {
  const { getThemeColor, config: { isDemo } } = useAppContext();
  const barChartRef = useRef(null);
  const [barData, setBarData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 데이터 가져오는 useEffect
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await avgRequest({
          timeZone: clientTimeZone,
          type: 'api',
          demo: isDemo
        });
        if (cancelled) return;
        // 응답 데이터 처리 (response.data.data로 변경)
        const data = response?.data || {};
        const uniqueCompanies = new Set();
        const formattedData = [];

        for (let i = 1; i <= 5; i++) {
          const compName = data[`top${i}_company`];
          if (compName && !uniqueCompanies.has(compName)) {
            uniqueCompanies.add(compName);
            formattedData.push({
              name: compName,
              process: data[`top${i}_avg_req`] || 0,
              min: data[`top${i}_min_req`] || 0,
              max: data[`top${i}_max_req`] || 0,
              isEmpty: false
            });
          }
        }

        while(formattedData.length < 5) {
          formattedData.push({ name: '', process: 0, min: 0, max: 0, isEmpty: true });
        }

        const sortedData = formattedData.reverse();

        setBarData(formattedData);
      } catch (error) {
        if (cancelled) return;
        console.error('API 평균 요청량 데이터 불러오기 실패:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isDemo]);

  const getBarOption = () => ({
    color: [getThemeColor('primary'), getThemeColor('info')],
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        const customerName = params[0].name; // 고객사 이름
        const averageProcess = params[0].value; // 처리량 평균
        const minProcess = barData[params[0].dataIndex]?.min || 0; // 최소 처리량
        const maxProcess = barData[params[0].dataIndex]?.max || 0; // 최대 처리량

        return `${customerName}<br/>요청량 평균: ${formatCountStyle3(
          averageProcess
        )}<br/>최소 요청량: ${formatCountStyle3(
          minProcess
        )}<br/>최대 요청량: ${formatCountStyle3(maxProcess)}<br/>`;
      },
      axisPointer: { type: 'shadow' }
    },
    legend: { show: false },
    xAxis: {
      type: 'value',
      axisLabel: {
        color: getThemeColor('gray-500')
        // formatter: (value) => formatCountStyle3(value), // formatCountStyle3 적용
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: getThemeColor('gray-200')
        }
      }
    },
    yAxis: {
      type: 'category',
      data: barData.map(d => d.name),
      axisLabel: { color: getThemeColor('gray-500') }
    },
    series: [
      {
        name: '요청량 평균', // 시리즈 이름 수정
        type: 'bar',
        showBackground: true,
        backgroundStyle: { color: 'rgba(255, 255, 255, 0.05)' },
        data: barData.map((d, index) => {
          return {
            value: d.process,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: getCompanyColorRgba(d.name, 0.3) },
                { offset: 1, color: getCompanyColor(d.name) }
              ]),
              borderRadius: [0, 8, 8, 0]
            }
          };
        }),
      }
    ],
    grid: {
      left: '3%',
      right: '10%',
      bottom: '12%',
      top: '3%',
      containLabel: true
    }
  });

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header style={{ borderBottom: 'none', backgroundColor: 'transparent', padding: '20px 24px' }}>
        <h5 style={{ margin: 0, flexWrap: 'nowrap', whiteSpace: 'nowrap', fontWeight: 700, color: '#111827' }}>
          API 평균 요청량 상위
        </h5>
        <Badge>최근 1주일</Badge>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <div style={{ width: '100%', height: '15rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        <div style={{ width: '100%', display: isLoading ? 'none' : 'block' }}>
          <ReactEchart
            ref={barChartRef}
            echarts={echarts}
            option={getBarOption()}
            style={{ magin: '2rem', width: '100%', height: '20rem' }}
          />
        </div>
      </Card.Body>
    </Card>
  );
}

// 1안 - 뱃지 줄바꿈
const CardHeader = styled(Card.Header)`
  display: flex;
  align-items: center;
  padding-bottom: 0;
  gap: 8px;

  @media (min-width: 1540px) and (max-width: 2560px) {
    // flex-direction: column;
    align-items: start;
    gap: 4px;
  }
`;

// 2안 - 뱃지 내부에서 줄바꿈
const Badge = styled.span`
  margin-top: 0;
  margin-bottom: 0;
  padding: 2px 8px;
  background-color: #a1c4e3; // 배경색 파란색
  color: white; // 텍스트 색상 흰색
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  flex-wrap: nowrap; // 한 줄로 표시
  white-space: nowrap; // 한 줄로 유지

  /* @media (min-width: 1540px) and (max-width: 2560px) {
    white-space: normal;
    word-break: keep-all;
    text-align: center;
  } */
`;
